/**
 * BESTANDTEILE FESTLEGEN — reine Hilfslogik des BestandteileEditor.
 *
 * Der Editor selbst ist eine React-Komponente; das Projekt hat kein jsdom,
 * Dialogtests sind also nicht moeglich. Alles, was an der Liste etwas
 * VERAENDERT (hinzufuegen, umbenennen, Quelle wechseln, verschieben,
 * Vorlage uebernehmen) und alles, was PRUEFT, steht deshalb hier — rein,
 * ohne React und ohne Supabase, in Node testbar.
 *
 * Die fachlichen Regeln (Key-Erzeugung, Normalisieren, E5-Pruefung) liegen in
 * services/bestandteile/bestandteilLogic.ts. Hier wird nur zusammengesetzt;
 * nichts davon darf eine zweite Lesart einer Regel werden.
 *
 * Grundsaetze, die jede Funktion einhaelt:
 *  - Immer eine NEUE Liste, nie die Eingabe veraendern (React-Zustand).
 *  - Ein Key aendert sich nie — weder beim Umbenennen noch beim Wechsel der
 *    Quelle. Eintraege (`position_bestandteil_eintraege`) verweisen ueber ihn.
 *  - Neue Keys kollidieren nie mit `belegteKeys` (aktuelle, frueher entfernte
 *    und in Eintraegen benutzte Keys). Ein neuer Bestandteil mit einem alten
 *    Key erbte sonst still dessen Eintraege.
 */
import type { BestandteilQuelle, FlatRateCoverage, PositionsBestandteil, ServiceAssignment, ServiceItem } from '../../types';
import {
    BESTANDTEIL_AB_VORGABE,
    BESTANDTEIL_QUELLE_LABEL,
    hatBestandteile,
    istModulQuelle,
    kopiereVorlage,
    neuerBestandteilKey,
    pruefeDefinition,
    type AusgabeAnlass,
} from '../../services/bestandteile/bestandteilLogic';
import { KONTINGENT_MAX, parseStunden } from '../../services/bestandteile/stundenLogic';
import { gueltigesKontingent, kontingentFuerKopie } from '../../services/bestandteile/kontingentKopie';

// ─────────────────────────────────────────────────────────────────────────────
// Liste bearbeiten
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Vorbelegtes Label beim Hinzufuegen: der Name der Quelle. 'hand' hat keinen
 * — „Von Hand" ist keine Bezeichnung, der Name eines Handgriffs („Abstimmung
 * Bauherr") ist frei.
 */
export const vorgabeLabel = (quelle: BestandteilQuelle): string =>
    quelle === 'hand' ? '' : BESTANDTEIL_QUELLE_LABEL[quelle] ?? '';

/** Nur echte Elemente — der Editor bekommt `wert` u. U. roh aus jsonb. */
export const editierbareListe = (wert: unknown): PositionsBestandteil[] =>
    Array.isArray(wert)
        ? (wert.filter((b) => b && typeof b === 'object' && !Array.isArray(b)) as PositionsBestandteil[])
        : [];

const vergebeneKeys = (liste: readonly PositionsBestandteil[], belegteKeys: readonly string[] = []): string[] => [
    ...(belegteKeys || []),
    ...liste.map((b) => String(b?.key ?? '')),
];

/**
 * Haengt einen Bestandteil an. Der Key entsteht EINMAL hier aus dem Label
 * (neuerBestandteilKey) und bleibt danach fuer immer. Leeres Label: Liste
 * unveraendert (der Knopf ist dann ohnehin gesperrt).
 */
export const fuegeBestandteilHinzu = (
    liste: readonly PositionsBestandteil[],
    eingabe: { label: string; quelle: BestandteilQuelle },
    belegteKeys: readonly string[] = [],
): PositionsBestandteil[] => {
    const label = String(eingabe?.label ?? '').trim();
    if (!label) return [...liste];
    const key = neuerBestandteilKey(label, vergebeneKeys(liste, belegteKeys));
    return [...liste, { key, label, quelle: eingabe.quelle }];
};

/** Neues Label — der Key bleibt (Eintraege verweisen auf ihn, nicht auf den Namen). */
export const benenneBestandteilUm = (
    liste: readonly PositionsBestandteil[],
    key: string,
    label: string,
): PositionsBestandteil[] => liste.map((b) => (b.key === key ? { ...b, label } : b));

/**
 * Quelle wechseln. Das eigene `ab` faellt weg — es gehoerte zur alten Quelle,
 * fuer die neue gilt deren Vorgabe (bei 'hand' darf es gar keins geben,
 * pruefeDefinition: ab_bei_hand). Das Label folgt nur, solange es noch das
 * vorbelegte der alten Quelle ist; einen selbst vergebenen Namen ueberschreibt
 * ein Quellenwechsel nie. Der Key bleibt.
 */
export const wechsleBestandteilQuelle = (
    liste: readonly PositionsBestandteil[],
    key: string,
    quelle: BestandteilQuelle,
): PositionsBestandteil[] =>
    liste.map((b) => {
        if (b.key !== key) return b;
        const { ab: _altesAb, ...rest } = b;
        const altVorgabe = vorgabeLabel(b.quelle);
        const neuVorgabe = vorgabeLabel(quelle);
        const label = altVorgabe && String(b.label ?? '').trim() === altVorgabe && neuVorgabe ? neuVorgabe : b.label;
        return { ...rest, label, quelle };
    });

/**
 * „Erledigt ab" setzen (nur Modulquelle). Gespeichert wird `ab` nur, wenn es
 * von der Vorgabe der Quelle abweicht (BESTANDTEIL_AB_VORGABE, E3) — so
 * bleibt die Vorgabe die Vorgabe und `effektivesAb` die einzige Lesart.
 */
export const setzeBestandteilAb = (
    liste: readonly PositionsBestandteil[],
    key: string,
    ab: AusgabeAnlass,
): PositionsBestandteil[] =>
    liste.map((b) => {
        if (b.key !== key || !istModulQuelle(b.quelle)) return b;
        const { ab: _altesAb, ...rest } = b;
        const vorgabe = BESTANDTEIL_AB_VORGABE[b.quelle as Exclude<BestandteilQuelle, 'hand'>];
        return ab === vorgabe ? rest : { ...rest, ab };
    });

/** Eine Stelle nach oben (-1) oder unten (+1); am Rand unveraendert. */
export const verschiebeBestandteil = (
    liste: readonly PositionsBestandteil[],
    key: string,
    richtung: -1 | 1,
): PositionsBestandteil[] => {
    const von = liste.findIndex((b) => b.key === key);
    const nach = von + richtung;
    if (von < 0 || nach < 0 || nach >= liste.length) return [...liste];
    const neu = [...liste];
    [neu[von], neu[nach]] = [neu[nach], neu[von]];
    return neu;
};

export const entferneBestandteil = (liste: readonly PositionsBestandteil[], key: string): PositionsBestandteil[] =>
    liste.filter((b) => b.key !== key);

/** Was der Editor meldet: leere Liste → `null` („keine Bestandteile", ausdruecklich). */
export const alsBestandteilWert = (liste: readonly PositionsBestandteil[]): PositionsBestandteil[] | null =>
    liste.length > 0 ? [...liste] : null;

// ─────────────────────────────────────────────────────────────────────────────
// Vorlage uebernehmen
// ─────────────────────────────────────────────────────────────────────────────

export interface VorlagenUebernahme {
    /** Die neue Liste (tiefe Kopie der Vorlage). */
    liste: PositionsBestandteil[];
    /** Keys mit Eintrag, die dabei aus der Liste fallen — die Rueckfrage nennt sie. */
    erledigtEntfernt: string[];
}

/**
 * Die Katalogvorlage uebernehmen (ersetzt die Liste; ob gefragt wird,
 * entscheidet der Editor: bei nicht leerer Liste immer).
 *
 * Keys: Kopie statt Verweis (Kernregel 8), Keys der Vorlage bleiben —
 * dieselben Keys ueber Projekte hinweg machen Auswertungen erst moeglich. Mit
 * zwei Ausnahmen, beide aus „ein Key wird nie neu vergeben":
 *  - Steht derselbe Key schon in der AKTUELLEN Liste, bleibt er: es ist
 *    derselbe Bestandteil, seine Eintraege gelten weiter.
 *  - Steht er nur in `belegteKeys` (frueher entfernt, verwaiste Eintraege),
 *    bekommt das Element einen frischen Key — sonst erbte es still einen
 *    alten Erledigt-Eintrag.
 */
export const uebernimmVorlage = (
    aktuell: readonly PositionsBestandteil[],
    vorlage: PositionsBestandteil[] | null | undefined,
    belegteKeys: readonly string[] = [],
    keysMitEintrag: readonly string[] = [],
): VorlagenUebernahme => {
    const kopie = kopiereVorlage(vorlage) ?? [];
    const klein = (k: string) => String(k ?? '').trim().toLowerCase();
    const aktuelleKeys = new Set(aktuell.map((b) => klein(b.key)));
    const belegt = new Set((belegteKeys || []).map(klein));
    // Reserviert fuer frisch erzeugte Keys: alles je Benutzte plus die Keys
    // der Vorlage selbst, damit ein Ersatz-Key nie einen spaeteren trifft.
    const reserviert = [...(belegteKeys || []), ...aktuell.map((b) => b.key), ...kopie.map((b) => b.key)];
    const liste = kopie.map((b) => {
        const k = klein(b.key);
        if (aktuelleKeys.has(k) || !belegt.has(k)) return b;
        const frisch = neuerBestandteilKey(b.label, reserviert);
        reserviert.push(frisch);
        return { ...b, key: frisch };
    });
    const neueKeys = new Set(liste.map((b) => b.key));
    const mitEintrag = new Set(keysMitEintrag || []);
    const erledigtEntfernt = aktuell.filter((b) => mitEintrag.has(b.key) && !neueKeys.has(b.key)).map((b) => b.key);
    return { liste, erledigtEntfernt };
};

// ─────────────────────────────────────────────────────────────────────────────
// Pruefen
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Pruef-Position fuer Listen OHNE echte Projektposition (Katalogvorlage,
 * Angebotszeile, neue Katalogposition). pruefeDefinition prueft die Liste
 * immer gegen eine Position; hier eine neutrale mit Menge 1 und den Feldern,
 * die die Positionsregeln lesen (Pauschale). So gibt es EINE Regelstelle,
 * nicht eine zweite fuer Vorlagen.
 */
export const pruefPosition = (felder: {
    quantityContracted?: number;
    unit?: string;
    assignment?: ServiceAssignment;
    flatRate?: FlatRateCoverage;
    /** Eventualposition (NEP) — pruefeDefinition lehnt Bestandteile dort ab (nep_position). */
    is_optional?: boolean;
} = {}): ServiceItem => ({
    id: '',
    lvPosition: '',
    title: '',
    unit: felder.unit ?? '',
    unitPrice: 0,
    quantityContracted: felder.quantityContracted ?? 1,
    quantityUsed: 0,
    status: 'open',
    ...(felder.assignment ? { assignment: felder.assignment } : {}),
    ...(felder.flatRate ? { flatRate: felder.flatRate } : {}),
    ...(felder.is_optional === true ? { is_optional: true } : {}),
});

/** Fehlertext zur Liste (pruefeDefinition) oder null. Ohne Position: neutrale Pruef-Position. */
export const pruefeBestandteilListe = (
    liste: readonly PositionsBestandteil[],
    service?: ServiceItem | null,
): string | null => {
    const r = pruefeDefinition(service ?? pruefPosition(), [...liste]);
    return r.ok === false ? r.text : null;
};

/**
 * Warum diese Position grundsaetzlich KEINE Bestandteile haben kann (Menge
 * nicht 1, Monatspauschale, pauschal gedeckt) — oder null. Geprueft wird mit
 * einem gueltigen Probe-Bestandteil, damit allein die Positionsregeln greifen.
 */
export const positionsSperreFuerBestandteile = (service: ServiceItem): string | null => {
    const r = pruefeDefinition(service, [{ key: 'probe', label: 'Probe', quelle: 'hand' }]);
    return r.ok === false ? r.text : null;
};

export interface PositionsDefinitionsFehler {
    serviceId: string;
    titel: string;
    text: string;
}

/**
 * Alle Positionen eines LV, deren Bestandteile nicht zur Position passen
 * (z. B. Menge 2 bei einer Paketposition, E5). `nurIds`: nur diese pruefen —
 * der Nachtrag prueft nur, was im Dialog neu oder geaendert ist, sonst
 * sperrte ein Altfall jeden Nachtrag.
 */
export const definitionsFehlerImLv = (
    services: readonly ServiceItem[],
    nurIds?: ReadonlySet<string>,
): PositionsDefinitionsFehler[] =>
    (services || []).flatMap((s) => {
        if (!hatBestandteile(s)) return [];
        if (nurIds && !nurIds.has(String(s.id))) return [];
        const r = pruefeDefinition(s, s.bestandteile as PositionsBestandteil[]);
        if (r.ok !== false) return [];
        const titel = [s.lvPosition, s.title].filter(Boolean).join(' ') || 'Position';
        return [{ serviceId: String(s.id), titel, text: r.text }];
    });

/**
 * Kopie einer Position aus Katalog/Vorlage mit TIEFER Kopie der
 * Bestandteile (Kernregel 8). Ohne Vorlage faellt der Schluessel ganz weg —
 * ein ausgeschriebenes `bestandteile: undefined` braucht niemand.
 *
 * Das Stundenkontingent reist NUR mit den Bestandteilen (S1,
 * docs/stundenkontingent-vertrag.md): bleibt von der Vorlage nichts uebrig,
 * faellt es mit weg, und ein ungueltiger Wert reist nie mit
 * (kontingentFuerKopie). Sonst traege die neue Position ein Kontingent, das
 * keine Oberflaeche zeigt.
 */
export const mitVorlagenKopie = <T extends { bestandteile?: PositionsBestandteil[] | null; stundenKontingent?: number | null }>(
    item: T,
): T => {
    const { bestandteile, stundenKontingent, ...rest } = item;
    const kopie = kopiereVorlage(bestandteile);
    return (kopie ? { ...rest, bestandteile: kopie, ...kontingentFuerKopie({ bestandteile: kopie }, stundenKontingent) } : rest) as T;
};

/**
 * Was eine Aenderung der Bestandteile an der Position setzt — fuer den
 * `onChange` jedes BestandteileEditor, der neben einem Kontingentfeld steht.
 * Faellt die Liste weg (Editor meldet `null`), faellt das Kontingent mit (S1):
 * `stundenKontingent: undefined` heisst beim Speichern „kein Schluessel"
 * (projectLv.stundenKontingentZeile), an einer neuen Position also „kein
 * Kontingent". Eine bestehende Position behaelt ihren Wert ohnehin in der
 * Datenbank (S10) — den aendert nur ein Admin im Bestandteile-Dialog.
 *
 * `leer`: was „keine Bestandteile" an dieser Stelle heisst — neue Position
 * `undefined` (kein Schluessel), bestehende `null` (sonst bliebe die
 * Definition in der Datenbank stehen).
 */
export function bestandteilePatch(
    neu: PositionsBestandteil[] | null | undefined,
): { bestandteile: PositionsBestandteil[] | undefined; stundenKontingent?: undefined };
export function bestandteilePatch(
    neu: PositionsBestandteil[] | null | undefined,
    leer: null | undefined,
): { bestandteile: PositionsBestandteil[] | null | undefined; stundenKontingent?: undefined };
export function bestandteilePatch(
    neu: PositionsBestandteil[] | null | undefined,
    leer: null | undefined = undefined,
): { bestandteile: PositionsBestandteil[] | null | undefined; stundenKontingent?: undefined } {
    return Array.isArray(neu) && neu.length > 0 ? { bestandteile: neu } : { bestandteile: leer, stundenKontingent: undefined };
}

// ─────────────────────────────────────────────────────────────────────────────
// Stundenkontingent (Eingabefeld neben dem Editor)
// ─────────────────────────────────────────────────────────────────────────────

export interface KontingentEingabe {
    /** Der gueltige Wert; `undefined` = kein Kontingent (leer ODER ungueltig). */
    wert: number | undefined;
    /** Fehlertext bei ungueltiger Eingabe, sonst null. Leer ist kein Fehler. */
    fehler: string | null;
}

export const KONTINGENT_FEHLER =
    `Bitte eine Stundenzahl über 0 bis ${KONTINGENT_MAX.toLocaleString('de-DE')} eingeben, höchstens zwei Nachkommastellen (z. B. 62,5). So wird kein Kontingent übernommen.`;

/**
 * Text des Kontingentfelds → Wert. Komma oder Punkt (parseStunden, S9),
 * Obergrenze KONTINGENT_MAX. Leer = kein Kontingent, kein Fehler. Ungueltig =
 * ebenfalls KEIN Wert (ein ungueltiger Wert wird nie gesendet) — der
 * Fehlertext sagt das ausdruecklich, damit niemand einen still verworfenen
 * Wert fuer gespeichert haelt.
 */
export const leseKontingentEingabe = (text: string | null | undefined): KontingentEingabe => {
    const roh = String(text ?? '');
    if (!roh.trim()) return { wert: undefined, fehler: null };
    const wert = parseStunden(roh, { max: KONTINGENT_MAX });
    return wert === null ? { wert: undefined, fehler: KONTINGENT_FEHLER } : { wert, fehler: null };
};

/**
 * Wert → Text fuers Feld: mit Komma, OHNE Tausenderpunkt — „12.345" laese
 * parseStunden als drei Nachkommastellen und lehnte es ab (formatStunden
 * gruppiert ab 10 000, taugt hier also nicht). Kein gueltiger Wert → ''.
 */
export const kontingentAlsText = (wert: unknown): string => {
    const h = gueltigesKontingent(wert);
    return h === undefined ? '' : String(h).replace('.', ',');
};
